import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import BarChartShadowSvg from "../BarChartChadowSvg";
import FA from "../../../containers/fa";
import "./ClassAveragesBarChart.scss";

const ClassAveragesBarChart = ({
  classAverageData,
  selectedLesson,
  learners,
  isLoading,
}) => {
  const [parsedAttnData, setParsedAttnData] = useState([]);
  const [parsedPerformData, setParsedPerformData] = useState([]);
  const [chartLabels, setChartLabels] = useState(["Class Average"]);
  const [chartReady, setChartReady] = useState(false);

  const roundNum = (num) => {
    return Math.round(num * 100);
  };

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Attention",
        data: parsedAttnData,
        backgroundColor: "rgba(255, 99, 132, 0.2)",
      },
      {
        label: "Performance",
        data: parsedPerformData,
        backgroundColor: "rgba(54, 162, 235, 0.2)",
      },
    ],
  };

  useEffect(() => {
    if (isLoading) {
      setChartReady(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      // clear the chart if no learners took the current lesson, no lesson was
      // selected, or no learners exist.
      if (
        !classAverageData ||
        !selectedLesson ||
        !learners ||
        (learners && learners.length === 0)
      ) {
        setParsedAttnData([]);
        setParsedPerformData([]);
        setChartLabels(["Class Average"]);
        setChartReady(true);
        return;
      }

      let labels = ["Class Average"];
      let newClassAttnData = roundNum(classAverageData.sum_attention);
      let newClassPerformData = roundNum(classAverageData.sum_performance);
      let newStudentAttnData = [];
      let newStudentPerformData = [];
      for (let item of classAverageData.by_user) {
        labels.push(item.username);
        newStudentAttnData.push(roundNum(item.sum_attention));
        newStudentPerformData.push(roundNum(item.sum_performance));
      }
      setParsedAttnData([newClassAttnData, ...newStudentAttnData]);
      setParsedPerformData([newClassPerformData, ...newStudentPerformData]);
      setChartLabels(labels);
      setChartReady(true);
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [classAverageData, selectedLesson, learners, isLoading]);

  const showLoading = isLoading || !chartReady;

  return (
    <div id='class_averages_bar_chart_container' className='visible-scrollbar'>
      <div
        id='class_averages_bar_chart'
        className='chart-container'
        style={{
          width: "100%",
          minWidth:
            parsedAttnData.length - 1 < 6
              ? "100%"
              : `${(parsedAttnData.length - 1) * 50}px`,
          height: "350px",
        }}
      >
        {showLoading ? (
          <div
            id='chart_loading_state'
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <FA icon='spinner' spin size='2x' color='#666' />
            <p style={{ marginTop: "10px", color: "#666" }}>Loading chart...</p>
          </div>
        ) : classAverageData &&
          classAverageData.by_user.length !== 0 &&
          selectedLesson &&
          learners &&
          learners.length !== 0 ? (
          <Bar
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  ticks: {
                    callback: (yValue) => {
                      return Math.round(yValue) + "%";
                    },
                  },
                  beginAtZero: true,
                  suggestedMax: 100,
                },
              },
              plugins: {
                datalabels: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      return (
                        context.dataset.label + ": " + context.parsed.y + "%"
                      );
                    },
                  },
                },
                title: {
                  display: true,
                  text: `${selectedLesson.name} - Average Performance + Attention`,
                  font: {
                    size: 12,
                  },
                },
              },
            }}
          />
        ) : learners && learners.length === 0 ? (
          <div id='no_class_avg_data'>
            <BarChartShadowSvg />
            <p>This class has no learners.</p>
          </div>
        ) : (
          <div id='no_class_avg_data'>
            <BarChartShadowSvg />
            <p>Select a lesson.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassAveragesBarChart;
